import './LoiPreparationModal.scss';
import React, { useEffect, useRef, useState } from 'react';
import { Button, ModalBody, ModalHeader } from 'reactstrap';
import Rodal from '../../../components/Rodal/Rodal';
import { useStateSelector } from '../../../store/selectors';
import { fetchLoiPreparationUrl, fetchLoiPreparationStatus } from '../../../slices/marketplace/loi-preparation/loi-preparation-api';
import Helpers from '../../../utils/helper';
import createNotification from '../../../utils/createNotification';

const LOI_PREPARATION_MODAL_WIDTH = 480;

export interface LoiPreparationModalProps {
    isVisible: boolean;
    onClose: () => void;
    engagementId: number;
    eventId: string;
    buyerId: number;
}

export const LoiPreparationModal = (props: LoiPreparationModalProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [prepareUrl, setPrepareUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // SignRequest's prepare page can't run inside an iframe (its CSRF cookie is
    // third-party there and gets blocked), so it must open as a top-level tab.
    const prepWindowRef = useRef<Window | null>(null);

    useEffect(() => {
        if (!props.isVisible || !axios) {
            return;
        }
        setIsLoading(true);
        setPrepareUrl(null);
        setErrorMessage(null);
        fetchLoiPreparationUrl(axios, props.engagementId, props.eventId, props.buyerId)
            .then((url) => {
                setPrepareUrl(url);
                prepWindowRef.current = window.open(url, '_blank');
            })
            .catch((err: unknown) => {
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Could not load LOI preparation. Please try again.';
                setErrorMessage(message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [props.isVisible, axios, props.engagementId, props.eventId, props.buyerId]);

    useEffect(() => {
        if (!props.isVisible || !axios) {
            return;
        }
        let cancelled = false;
        const intervalId = setInterval(async () => {
            if (cancelled) return;
            try {
                const isPrepared = await fetchLoiPreparationStatus(
                    axios,
                    props.engagementId,
                    props.eventId,
                    props.buyerId,
                );
                if (isPrepared && !cancelled) {
                    if (prepWindowRef.current && !prepWindowRef.current.closed) {
                        prepWindowRef.current.close();
                    }
                    createNotification('LOI prepared successfully!', 'success');
                    props.onClose();
                }
            } catch {
            }
        }, 3000);
        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [props.isVisible, axios, props.engagementId, props.eventId, props.buyerId, props.onClose]);

    const handleOpenTab = () => {
        if (!prepareUrl) {
            return;
        }
        if (prepWindowRef.current && !prepWindowRef.current.closed) {
            prepWindowRef.current.focus();
            return;
        }
        prepWindowRef.current = window.open(prepareUrl, '_blank');
    };

    const renderBody = () => {
        if (isLoading) {
            return (
                <div className="loi-preparation-loading">
                    {Helpers.renderTableLoader()}
                </div>
            );
        }
        if (errorMessage) {
            return <div className="loi-preparation-error">{errorMessage}</div>;
        }
        if (prepareUrl) {
            return (
                <div className="loi-preparation-waiting">
                    <p>
                        The LOI preparation page has opened in a new browser
                        tab. Place the signature fields and send the document
                        there.
                    </p>
                    <p>
                        This window will close automatically once preparation
                        is confirmed.
                    </p>
                    <Button color="primary" onClick={handleOpenTab}>
                        Open SignRequest
                    </Button>
                    <p className="loi-preparation-hint">
                        If a new tab didn't open, use the button above.
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="loi-preparation-modal">
            <Rodal
                visible={props.isVisible}
                onClose={props.onClose}
                className="checkout-modal loi-preparation-rodal"
                width={LOI_PREPARATION_MODAL_WIDTH}
                customStyles={{ maxWidth: '90vw' }}
                animation="fade"
                showMask={true}
                centered>
                <ModalHeader>
                    <span>Prepare LOI for Signing</span>
                </ModalHeader>
                <ModalBody>
                    {renderBody()}
                </ModalBody>
            </Rodal>
        </div>
    );
};
